// @ts-strict-ignore
// eslint-disable-next-line max-classes-per-file
import React, { useRef, useState } from 'react';
import { TouchableOpacity } from 'react-native';
import { Box, Inline } from '@styleseat/ui';
import {
  BodyText,
  HeaderText,
  Stack,
  ModalBox,
} from '../../../ui';
import AvatarIcon from '../../../AvatarIcon';
import { PortalOverlayHeaderCloseBtn } from '../../../ui/PortalOverlay';

import debouncer from '../../../../modules/debouncer';
import ImageSwiper, { SubsetOfFieldsFromImageSwiper } from '../../images/ImageSwiper';
import { ClientProviderProfile } from '../../../../api/ProfileClientView';
import { PublicProvider } from '../../../../api/Providers';
import { getBusinessName } from '../../../../api/ProfileClientView/ProfileClientView';
import { getWidth } from '../../../../modules/Responsive';

const SCROLL_LAYOUT_DELAY = 500;

export type ProfileImageSwiperProps = {
  /** Called when the user wishes to close the modal */
  onCloseRequest: () => void;
  /** Called when clicking the avatar info in the header. */
  onAvatarPress?: () => void;
  /** Provider to display in the upper left of the modal */
  provider: ClientProviderProfile | PublicProvider;
  /** Any additional components to display immediately below the image carousel */
  children?: React.ReactNode;
  /** The absolute URL of the provider's profile photo */
  providerProfilePhoto?: string;
  /** A function for adding a fixed compnent (like a Button) to the bottom of the modal */
  renderBottomContent?: () => JSX.Element;
  /** The total number of images */
  imageTotal: number;
  displayHeader?: boolean;
} & SubsetOfFieldsFromImageSwiper<(
  'images'
  | 'onIndexChanged'
  | 'formatImageUrl'
  | 'onImagePress'
  | 'initialIndex'
)>;

/**
 * A modal which displays a swipeable set of images, provider details, and any additional components
 * which are supplied as children.
 */
const ProfileImageSwiper: React.FCWithChildren<ProfileImageSwiperProps> = props => {
  const {
    onCloseRequest,
    onAvatarPress,
    provider,
    providerProfilePhoto,
    renderBottomContent,
    children,
    onIndexChanged,
    images = [],
    formatImageUrl,
    onImagePress,
    initialIndex,
    imageTotal,
    displayHeader = true,
  } = props;
  const locationName = getBusinessName(provider);

  const handleAvatarPress = () => {
    if (onAvatarPress) {
      onAvatarPress();
    }
  };

  const [width, setWidth] = useState(getWidth() || 0);

  const tmt = useRef<ReturnType<typeof debouncer>>(debouncer(SCROLL_LAYOUT_DELAY));

  return (
    <React.Fragment>
      {displayHeader && (
        <TouchableOpacity onPress={handleAvatarPress}>
          <Inline
            alignItems="flex-start"
            testID="gallery-image-modal-avatar"
          >
            <Box padding={12} flexGrow={1} flexShrink={1}>
              <Inline space={8}>
                <AvatarIcon
                  icon={providerProfilePhoto}
                  name={provider.name}
                  sizeClass="extra-small"
                />
                <Stack
                  space={0}
                  alignItems="flex-start"
                  justifyContent="center"
                >
                  <HeaderText level={4}>
                    {provider.name}
                  </HeaderText>
                  {!!locationName && (
                    <BodyText appearance="smaller">
                      {locationName}
                    </BodyText>
                  )}
                </Stack>
              </Inline>
            </Box>
            <PortalOverlayHeaderCloseBtn onCloseRequest={onCloseRequest} />
          </Inline>
        </TouchableOpacity>
      )}
      <ModalBox
        scrollable
        padding={0}
        flexGrow={1}
        onLayout={e => {
          tmt.current(() => {
            setWidth(e.nativeEvent.layout.width);
          });
        }}
      >
        {width > 0 && (
          <ImageSwiper
            images={images}
            formatImageUrl={formatImageUrl}
            onImagePress={onImagePress}
            initialIndex={initialIndex}
            onIndexChanged={onIndexChanged}
            imageTotal={imageTotal}
            trackingParams={{
              source: 'profile',
            }}
          >
            {children}
          </ImageSwiper>
        )}
      </ModalBox>
      {renderBottomContent && (renderBottomContent())}
    </React.Fragment>
  );
};

ProfileImageSwiper.defaultProps = {
  children: null,
  providerProfilePhoto: null,
};

export default ProfileImageSwiper;
